import { LogUserActivity } from '@/apis/users/requests.ts'
import { Button } from '@/app/common/components/button/Button'
import { buttonVariants } from '@/app/common/components/button/buttonVariants'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import MultipleSelector, {
  ListItem,
} from '@/common/components/MultipleSelector.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import { LOG_TYPES } from '@/common/constants.ts'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { FilterIcon } from '@/common/icons/general.tsx'
import { cn } from '@/common/utilities.ts'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { useAssignTask } from '@/pages/tasks/hooks/useAssignTask.ts'
import { TaskQueries } from '@/queries/tasks/queries.ts'
import { TaskList } from '@/queries/tasks/requests.ts'
import { Task } from '@/queries/tasks/tasks.schema.ts'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { RootState } from '@/storage/store'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { TaskSortBy } from '../../../queries/tasks/requests'
import { DownIcon, UpIcon } from '../../common/icons/general'

const RECORDS_PER_PAGE = 10 as const
const THREE_MINUTES = 1000 * 60 * 3
const InitialTasks: TaskList = []

function getFullName(
  firstName?: string | null,
  lastName?: string | null
): string {
  return `${firstName ?? ''} ${lastName ?? ''}`
}
const columnHelper = createColumnHelper<Task>()

export default function AppealsListView() {
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedFilter, setSelectedFilter] = useState<ListItem[]>([])
  const [search, setsearch] = useState('')
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const [sort_by, setSortBy] = useState<undefined | TaskSortBy>(undefined)
  const [sort_order, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const navigate = useNavigate() // React Router's navigate hook
  const { mutate } = useAssignTask()
  const { fireAlert } = useAppAlert()

  const convertToProgramTime = (created_datetime: string) => {
    let date = new Date(created_datetime)
    if (
      selectedProgramYear?.global_timezone_code != undefined &&
      selectedProgramYear.global_timezone_code.trim() != ''
    ) {
      date = new Date(created_datetime + 'Z')
      date = toZonedTime(date, selectedProgramYear.global_timezone_code)
    }
    return format(date, 'MM/dd/yyyy, h:mm aa')
  }

  const sortDays = () => {
    const newOrder = sort_order == 'desc' ? 'asc' : 'desc'
    setSortOrder(newOrder)
    setSortBy({ submitted_at: newOrder })
  }

  const columns = [
    columnHelper.accessor(
      (row) => getFullName(row.applicant_first_name, row.applicant_last_name),
      {
        id: 'full_name',
        header: () => (
          <>
            <span className="block pl-4 2xl:hidden">Applicant</span>
            <span className="hidden pl-4 2xl:block">Applicant</span>
          </>
        ),
        cell: (info) => {
          const row = info.row.original
          return (
            <div className="flex items-center">
              <div className="w-3/4 sm:w-full font-bold pl-4">
                <div className="font-medium text-gray-900 flex justify-between items-center">
                  <span>
                    {' '}
                    {getFullName(
                      row.applicant_first_name,
                      row.applicant_last_name
                    )}{' '}
                  </span>
                </div>
                <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                  <div>
                    {getFullName(row.student_first_name, row.student_last_name)}
                  </div>
                </div>
                <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                  <div>{row.global_application_name}</div>
                </div>
              </div>
            </div>
          )
        },
      }
    ),
    columnHelper.accessor(
      (row) => getFullName(row.student_first_name, row.student_last_name),
      {
        id: 'student_full_name',
        header: () => <span>Student</span>,
        cell: (info) => <div>{info.getValue()}</div>,
      }
    ),
    columnHelper.accessor('category_name', {
      header: () => <span>Category</span>,
      cell: (info) => <div>{info.row.original.category_name}</div>,
    }),
    columnHelper.accessor('global_application_name', {
      header: () => <span>Flag Name</span>,
      cell: (info) => <div>{info.row.original.global_application_name}</div>,
    }),
    columnHelper.accessor('days_since_submitted', {
      header: () => (
        <span className="cursor-pointer" onClick={sortDays}>
          Days {sort_order == 'asc' ? <UpIcon /> : <DownIcon />}{' '}
        </span>
      ),
      cell: (info) => (
        <div>{info.row.original.submitted_at ? info.getValue() : ''}</div>
      ),
    }),
    columnHelper.accessor('updated_notes', {
      header: () => <span>Updated</span>,
      cell: (info) => <div>{info.getValue() ?? ''}</div>,
    }),
    columnHelper.accessor('submitted_at', {
      header: () => (
        <span className="cursor-pointer" onClick={sortDays}>
          Submitted At {sort_order == 'asc' ? <UpIcon /> : <DownIcon />}{' '}
        </span>
      ),
      cell: (info) => {
        const lastDateTimeStr = info.getValue()
          ? convertToProgramTime(info.getValue()!)
          : ''
        return <span>{lastDateTimeStr}</span>
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => {
        const row = info.row.original
        const taskId = row.task_id ?? null
        const scholarshipApplicationId = String(
          row.scholarship_application_id ?? null
        )
        const combinated_flags = row.show_combinated_flags
        const querystring = [
          row.application_flags_id ? `flag_id=${row.application_flags_id}` : '',
          row.student_id ? `student_id=${row.student_id}` : '',
          taskId ? `task_id=${taskId}` : '',
        ]
          .filter(Boolean)
          .join('&')
        return (
          // <Link
          //   to={{
          //     pathname: AppRoutePaths.applicants.applicant_details.application.replace(
          //       ':applicantId',
          //       `${row.scholarship_application_id}`),
          //     search: querystring, // Include student_id in the URL
          //   }}
          //   className={buttonVariants({ variant: 'primary' })}
          // >
          //   Review
          // </Link>
          <button
            onClick={() =>
              handleReviewClick(
                taskId,
                scholarshipApplicationId,
                querystring,
                combinated_flags
              )
            }
            className={buttonVariants({ variant: 'primary' })}
          >
            Review
          </button>
        )
      },
    }),
  ]
  const TaskListQuery = useQuery(
    TaskQueries.getPaginatedTasks(
      recordsPerPage,
      currentPage,
      search,
      selectedFilter.map((filter) => filter.id).join(','),
      sort_by,
      selectedProgramYear?.id,
      {
        enabled: true,
        placeholderData: keepPreviousData,
        refetchInterval: THREE_MINUTES,
        staleTime: 0,
        refetchOnWindowFocus: true,
      }
    )
  )
  const ApplicationFlagList = useQuery(TaskQueries.FetchApplicationFlags())

  const tableData = TaskListQuery.isFetching
    ? InitialTasks
    : (TaskListQuery.data?.data ?? InitialTasks)
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: TaskListQuery.data?.last_page,
  })
  const refetchTasksList = () =>
    TaskListQuery.refetch({ throwOnError: true, cancelRefetch: false })

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Viewed the Task List',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  useEffect(() => {
    if (TaskListQuery.data === undefined) {
      setCurrentPage(1)
    }
  }, [TaskListQuery.data, currentPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const handleReviewClick = async (
    taskId: number | null,
    scholarshipApplicationId: string | null,
    querystring?: string,
    combinated_flags?: string[] | null
  ) => {
    try {
      const validTaskId = taskId ?? 0
      mutate(validTaskId, {
        onSuccess: () => {
          navigate(
            {
              pathname:
                AppRoutePaths.applicants.applicant_details.application.replace(
                  ':applicantId',
                  `${scholarshipApplicationId}`
                ),
              search: querystring || '',
            },
            {
              state: {
                combinated_flags,
              },
              replace: true,
            }
          )
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          fireAlert({
            confirmBtn: true,
            variant: 'error',
            message:
              error?.response?.data?.error?.message ||
              'An unexpected error occurred.',
          }).then(async ({ confirmed }) => {
            if (confirmed) {
              await TaskListQuery.refetch({
                throwOnError: true,
                cancelRefetch: false,
              })
            }
          })
        },
      })
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <section aria-labelledby="tasks-list-title">
      <h1 id="tasks-list-title" className="sr-only">
        Task list
      </h1>
      <div className="w-full overflow-x-auto">
        <Card className="w-full">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              Tasks
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {TaskListQuery.isFetching
                  ? 'refreshing data...'
                  : TaskListQuery.data === undefined
                    ? 'last updated at: ' + format(new Date(), 'hh:mm:ss a')
                    : 'last updated at: ' +
                      format(TaskListQuery.dataUpdatedAt, 'hh:mm:ss a')}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <SearchInput
                  id="table_search"
                  placeholder="Search Tasks"
                  value={search}
                  onChange={(e) => setsearch(e.target.value)}
                />
                {ApplicationFlagList?.data?.data && (
                  <MultipleSelector
                    list={ApplicationFlagList.data?.data.sort((a, b) =>
                      a.name.localeCompare(b.name)
                    )}
                    selected={selectedFilter}
                    setSelected={setSelectedFilter}
                    icon={<FilterIcon className="text-primary" />}
                  />
                )}
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={TaskListQuery.isFetching}
                    onClick={refetchTasksList}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {TaskListQuery.isFetching
                      ? 'refreshing data...'
                      : 'last updated at: ' +
                        format(TaskListQuery.dataUpdatedAt, 'hh:mm:ss a')}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <table className="w-full table-auto">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <th
                        key={header.id}
                        className={cn(
                          'w-[17%] px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                          index != 0 &&
                            index != headerGroup.headers.length - 1 &&
                            'hidden md:table-cell',
                          index == 3 && 'w-[5%]',
                          index == 4 && 'w-[20%] text-center',
                          index > 3 && 'text-center'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {(function () {
                if (TaskListQuery.error) {
                  return (
                    <tr>
                      <td colSpan={5} className="text-center p-2">
                        {TaskListQuery.error.message}
                      </td>
                    </tr>
                  )
                } else {
                  return (
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell, index) => (
                            <td
                              key={cell.id}
                              className={cn(
                                'w-[17%] py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                                index != 0 &&
                                  index != row.getVisibleCells().length - 1 &&
                                  'hidden md:table-cell',
                                index == 3 && 'w-[5%]',
                                index == 4 && 'w-[20%] text-center',
                                index > 3 && 'text-center'
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  )
                }
              })()}
            </table>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={TaskListQuery.data?.total ?? 0}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </div>
    </section>
  )
}
